import { ChangeDetectionStrategy, Component, computed, inject, input, model, output } from '@angular/core';
import { MatDialog } from '@angular/material/dialog';
import { MatProgressSpinnerModule } from '@angular/material/progress-spinner';
import { CalendarEventDialogComponent } from './components/calendar-event-dialog/calendar-event-dialog.component';
import { CalendarHeaderComponent } from './components/calendar-header/calendar-header.component';
import { CalendarListViewComponent } from './components/calendar-list-view/calendar-list-view.component';
import { CalendarMonthViewComponent } from './components/calendar-month-view/calendar-month-view.component';
import { CalendarSettingsDialogComponent } from './components/calendar-settings-dialog/calendar-settings-dialog.component';
import { CalendarWeekViewComponent } from './components/calendar-week-view/calendar-week-view.component';
import {
  CalendarEvent,
  CalendarSettings,
  CalendarSlotClickEvent,
  CalendarSource,
  CalendarView,
  DEFAULT_CALENDAR_SETTINGS,
} from './models/calendar.types';

@Component({
  selector: 'aura-calendar',
  imports: [
    CalendarHeaderComponent,
    CalendarMonthViewComponent,
    CalendarWeekViewComponent,
    CalendarListViewComponent,
    MatProgressSpinnerModule,
  ],
  templateUrl: './calendar.component.html',
  host: {
    class: 'flex flex-col flex-1 min-h-0 w-full h-full',
  },
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class CalendarComponent {
  private readonly dialog = inject(MatDialog);

  readonly events = input<CalendarEvent[]>([]);
  readonly sources = model<CalendarSource[]>([]);
  readonly view = model<CalendarView>('month');
  readonly currentDate = model<Date>(new Date());
  readonly settings = model<CalendarSettings>({ ...DEFAULT_CALENDAR_SETTINGS });
  readonly isLoading = input<boolean>(false);
  readonly emptyMessage = input<string>('Aucun rendez-vous prévu pour le moment.');
  readonly autoOpenDialog = input<boolean>(true);
  readonly enableWheelNavigation = input<boolean>(true);

  readonly eventClick = output<CalendarEvent>();
  readonly slotClick = output<CalendarSlotClickEvent>();
  readonly sourceToggle = output<string>();

  readonly visibleEvents = computed<CalendarEvent[]>(() => {
    const srcList = this.sources();
    if (!srcList || srcList.length === 0) {
      return this.events();
    }
    const disabledSources = new Set(srcList.filter((s) => !s.visible).map((s) => s.id));
    return this.events().filter((e) => !e.sourceId || !disabledSources.has(e.sourceId));
  });

  private isWheelNavigating = false;
  private wheelCooldownTimeout?: ReturnType<typeof setTimeout>;

  onWheel(event: WheelEvent): void {
    if (!this.enableWheelNavigation() || this.isWheelNavigating) {
      return;
    }

    const absX = Math.abs(event.deltaX);
    const absY = Math.abs(event.deltaY);

    // Seuil minimal pour ignorer les micro-mouvements parasites
    const threshold = 18;
    if (absX < threshold && absY < threshold) {
      return;
    }

    const isHorizontal = absX > absY;
    const delta = isHorizontal ? event.deltaX : event.deltaY;
    const currentView = this.view();

    if (currentView === 'month') {
      event.preventDefault();
      this.triggerWheelNavigation(delta > 0 ? 'next' : 'previous');
      return;
    }

    if (currentView === 'week') {
      // 1. Défilement horizontal (swipe touchpad gauche/droite)
      if (isHorizontal) {
        event.preventDefault();
        this.triggerWheelNavigation(delta > 0 ? 'next' : 'previous');
        return;
      }

      // 2. Touche modificatrice (Shift ou Alt)
      if (event.shiftKey || event.altKey) {
        event.preventDefault();
        this.triggerWheelNavigation(delta > 0 ? 'next' : 'previous');
        return;
      }

      // 3. Détection de la zone ciblée
      const target = event.target as HTMLElement | null;
      const isHeaderScroll = !!target?.closest('aura-calendar-header, [data-calendar-week-header]');
      const scrollContainer = target?.closest('[data-calendar-week-scroll]') as HTMLElement | null;

      if (isHeaderScroll || !scrollContainer) {
        event.preventDefault();
        this.triggerWheelNavigation(delta > 0 ? 'next' : 'previous');
        return;
      }

      // 4. Si le conteneur n'a pas de défilement vertical nécessaire (la semaine tient entièrement)
      const isScrollable = scrollContainer.scrollHeight > scrollContainer.clientHeight + 10;
      if (!isScrollable) {
        event.preventDefault();
        this.triggerWheelNavigation(delta > 0 ? 'next' : 'previous');
        return;
      }

      // 5. Si le conteneur a un scroll vertical, navigation aux extrémités
      const atTop = scrollContainer.scrollTop <= 5;
      const atBottom =
        scrollContainer.scrollTop + scrollContainer.clientHeight >= scrollContainer.scrollHeight - 5;

      if ((delta < 0 && atTop) || (delta > 0 && atBottom)) {
        event.preventDefault();
        this.triggerWheelNavigation(delta > 0 ? 'next' : 'previous');
      }
    } else if (currentView === 'list') {
      const target = event.target as HTMLElement | null;
      const isHeaderScroll = !!target?.closest('aura-calendar-header');
      if (isHeaderScroll) {
        event.preventDefault();
        this.triggerWheelNavigation(delta > 0 ? 'next' : 'previous');
      }
    }
  }

  private triggerWheelNavigation(direction: 'next' | 'previous'): void {
    this.isWheelNavigating = true;

    if (direction === 'next') {
      this.onNext();
    } else {
      this.onPrevious();
    }

    clearTimeout(this.wheelCooldownTimeout);
    this.wheelCooldownTimeout = setTimeout(() => {
      this.isWheelNavigating = false;
    }, 400);
  }

  onPrevious(): void {
    const d = new Date(this.currentDate());
    if (this.view() === 'week') {
      d.setDate(d.getDate() - 7);
    } else {
      d.setMonth(d.getMonth() - 1);
    }
    this.currentDate.set(d);
  }

  onNext(): void {
    const d = new Date(this.currentDate());
    if (this.view() === 'week') {
      d.setDate(d.getDate() + 7);
    } else {
      d.setMonth(d.getMonth() + 1);
    }
    this.currentDate.set(d);
  }

  onToday(): void {
    const today = new Date();
    this.currentDate.set(today);
  }

  onViewChange(newView: CalendarView): void {
    this.view.set(newView);
  }

  onEventClicked(evt: CalendarEvent): void {
    this.eventClick.emit(evt);

    if (this.autoOpenDialog()) {
      this.dialog.open(CalendarEventDialogComponent, {
        data: { event: evt },
        width: '540px',
        maxWidth: '95vw',
        panelClass: 'aura-calendar-event-modal',
      });
    }
  }

  onSlotClicked(slot: CalendarSlotClickEvent): void {
    this.slotClick.emit(slot);
  }

  onOpenSettings(): void {
    const ref = this.dialog.open(CalendarSettingsDialogComponent, {
      data: { settings: this.settings() },
      width: '460px',
      maxWidth: '95vw',
      panelClass: 'aura-calendar-settings-modal',
    });

    ref.afterClosed().subscribe((updated?: CalendarSettings) => {
      if (updated) {
        this.settings.set(updated);
      }
    });
  }
}
